import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, ChevronRight, Check } from "lucide-react";
import { play, sfx } from "../../lib/audio";
import { Ruby } from "../lesson/Ruby";
import { Avatar } from "../Avatar";
import { SpeakPractice } from "./SpeakPractice";

const R = (ruby, text) => (ruby?.length ? ruby : [...(text || "")].map((c) => [c, ""]));
const ask = (s) => `${s.situation.replace(/[。！]$/, "")}，要怎麼說呢？`;
const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);
const FACE = { 媽媽: "👩", 爸爸: "👨", 阿嬤: "👵", 阿公: "👴", 老師: "👩‍🏫", 同學: "🧒", 新朋友: "🧒", 警衛阿姨: "👮‍♀️" };

export const NextBtn = ({ onClick, label = "下一步", testid = "sp-next-btn" }) => (
  <button data-testid={testid} onClick={onClick} className="kid-btn px-10 bg-[var(--sp)] text-white text-2xl flex items-center gap-2">{label} <ChevronRight /></button>
);

export const SceneIntro = ({ topic, onNext }) => {
  useEffect(() => { setTimeout(() => play(`${topic.title.replace(/[…]+/g, "")}。${topic.scene}`), 400); }, [topic]);
  return (
    <div className="flex flex-col items-center gap-6 max-w-3xl mx-auto" data-testid="sp-scene">
      <img src={topic.cover_image} alt="" className="w-full max-h-[46vh] object-cover rounded-[2rem] border-4 border-white shadow-xl" />
      <button data-testid="sp-scene-play-btn" onClick={() => play(topic.scene)} className="flex items-start gap-3 text-left bg-white rounded-3xl px-6 py-4 border-2 border-[var(--sp-soft-b)]">
        <Volume2 className="text-[var(--sp)] shrink-0 mt-1" /><span className="text-2xl sm:text-3xl leading-relaxed text-stone-800">{topic.scene}</span>
      </button>
      <NextBtn onClick={onNext} label="開始說中文" />
    </div>
  );
};

export const ListenCard = ({ s, emoji, onListen, onNext }) => {
  const [heard, setHeard] = useState(false);
  const hear = () => { setHeard(true); onListen(); play(s.text, s.audio_url); };
  useEffect(() => { const t = setTimeout(hear, 400); return () => clearTimeout(t); }, [s.sentence_id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-col items-center gap-7" data-testid="sp-listen">
      <div className="text-2xl sm:text-3xl text-stone-600 flex items-center gap-3"><span className="text-5xl">{emoji}</span>{s.situation}</div>
      <button data-testid="sp-listen-sentence-btn" onClick={hear} className="bg-white rounded-[2rem] border-4 border-[var(--sp-soft-b)] px-8 py-6 shadow-lg flex items-center gap-4">
        <Volume2 size={36} className="text-[var(--sp)] shrink-0" />
        <Ruby data={R(s.ruby, s.text)} className="text-5xl sm:text-6xl leading-[1.9] text-stone-900" testid="sp-listen-sentence" />
      </button>
      <p className="text-xl text-stone-500">聽一聽，看一看，再按喇叭聽一次</p>
      {heard && <NextBtn onClick={onNext} />}
    </div>
  );
};

const Options = ({ options, onPick, testid }) => {
  const [bad, setBad] = useState([]);
  const [ok, setOk] = useState(null);
  const pick = (o, i) => {
    if (ok !== null || bad.includes(i)) return;
    if (o.correct) { setOk(i); sfx("good"); play(o.text, o.url); setTimeout(() => onPick(true, bad.length === 0), 1100); }
    else { setBad((b) => [...b, i]); sfx("bad"); onPick(false, false); }
  };
  return (
    <div className="grid gap-3 w-full max-w-2xl">
      {options.map((o, i) => (
        <button key={o.text} data-testid={`${testid}-option-${i}`} onClick={() => pick(o, i)}
          className={`rounded-3xl border-4 px-6 py-3 text-left flex items-center gap-3 bg-white transition-colors ${ok === i ? "border-green-500 bg-green-50" : bad.includes(i) ? "border-stone-200 opacity-50 sp-shake" : "border-[var(--sp-soft-b)] hover:border-[var(--sp)]"}`}>
          {ok === i && <Check className="text-green-600 shrink-0" size={30} />}
          <Ruby data={R(o.ruby, o.text)} className="text-3xl sm:text-4xl leading-[1.9]" />
        </button>
      ))}
    </div>
  );
};

export const ChooseCard = ({ s, all, emoji, onAnswer, onNext }) => {
  const options = useMemo(() => shuffle([{ text: s.text, ruby: s.ruby, url: s.audio_url, correct: true },
    ...shuffle(all.filter((x) => x.sentence_id !== s.sentence_id)).slice(0, 2).map((x) => ({ text: x.text, ruby: x.ruby, url: x.audio_url }))]), [s, all]);
  useEffect(() => { setTimeout(() => play(ask(s)), 400); }, [s]);
  return (
    <div className="flex flex-col items-center gap-6" data-testid="sp-choose">
      <button data-testid="sp-choose-question-btn" onClick={() => play(ask(s))} className="text-3xl sm:text-4xl text-[var(--sp-deep)] flex items-center gap-3">
        <span className="text-5xl">{emoji}</span>{ask(s)}<Volume2 className="text-[var(--sp)]" />
      </button>
      <Options key={s.sentence_id} options={options} testid="sp-choose" onPick={(correct, first) => { onAnswer(correct); if (correct) onNext(first); }} />
    </div>
  );
};

const Bubble = ({ line, child }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className={`flex items-end gap-3 ${line.child ? "flex-row-reverse" : ""}`}>
    <span className="w-14 h-14 shrink-0 rounded-full bg-white border-2 border-[var(--sp-soft-b)] flex items-center justify-center text-3xl overflow-hidden">
      {line.child ? <Avatar value={child.avatar} /> : FACE[line.speaker] || "🙂"}
    </span>
    <button onClick={() => play(line.text)} className={`rounded-3xl px-5 py-2 max-w-[80%] text-left ${line.child ? "bg-[var(--sp)] text-white [&_.zy-mark]:text-white/90" : "bg-white border-2 border-[var(--sp-soft-b)]"}`}>
      {!line.child && <div className="text-sm text-stone-500">{line.speaker}</div>}
      <Ruby data={R(line.ruby, line.text)} className="text-2xl sm:text-3xl leading-[1.9]" />
    </button>
  </motion.div>
);

export const Dialogue = ({ topic, child, findSentence, onAnswer, onScore, onEvent, onNext }) => {
  const lines = topic.dialogue || [];
  const [n, setN] = useState(0);
  const [phase, setPhase] = useState("show");
  const cur = lines[n];
  useEffect(() => {
    if (cur && !cur.child) { play(cur.text); setPhase("show"); } else if (cur) setPhase("choose");
  }, [n]); // eslint-disable-line react-hooks/exhaustive-deps
  const options = useMemo(() => cur?.child ? shuffle([{ text: cur.text, ruby: cur.ruby, correct: true }, ...(cur.wrong || []).map((w, i) => ({ text: w, ruby: cur.wrong_ruby?.[i] }))]) : [], [cur]);
  const sid = cur?.child ? findSentence(cur.text) : null;
  const shown = lines.slice(0, cur?.child && phase === "choose" ? n : n + 1);
  return (
    <div className="flex flex-col gap-5 max-w-3xl mx-auto w-full" data-testid="sp-dialogue">
      <h2 className="text-3xl sm:text-4xl text-[var(--sp-deep)] text-center">說說看：小對話</h2>
      <div className="flex flex-col gap-3">
        <AnimatePresence>{shown.map((l, i) => <Bubble key={i} line={l} child={child} />)}</AnimatePresence>
      </div>
      <div className="flex flex-col items-center gap-4 mt-2">
        {cur?.child && phase === "choose" && (<>
          <p className="text-2xl text-stone-600">換你了！你要說什麼？</p>
          <Options key={n} options={options} testid="sp-dialogue" onPick={(correct, first) => { onAnswer(correct, sid); if (correct) { onScore(first); setPhase("say"); } }} />
        </>)}
        {cur?.child && phase === "say" && (
          <SpeakPractice key={n} text={cur.text} ruby={cur.ruby} testid="sp-dialogue" doneLabel="說好了"
            onRecorded={() => onEvent("record", sid)} onDone={() => { onEvent("speak", sid); n + 1 < lines.length ? setN(n + 1) : onNext(); }} />
        )}
        {cur && !cur.child && (n + 1 < lines.length ? <NextBtn testid="sp-dialogue-next-btn" onClick={() => setN(n + 1)} label="接下來" /> : <NextBtn testid="sp-dialogue-finish-btn" onClick={onNext} label="完成對話" />)}
      </div>
    </div>
  );
};
